import React, { useMemo } from "react";
import Link from "next/link";
import { useCasinoPage } from "../CasinoPageContext";

function slugifyTitle(title: string): string {
  return title
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-");
}

const BlogsSection = () => {
  const { website } = useCasinoPage();
  const parent = website?.homepageContent;

  const blog = parent?.blogsSection;
  const blogPosts = blog?.blogs || [];

  // Sort once by date (newest first) so indices are stable between
  // homepage, /blogs, and /blog-details.
  const sortedBlogs = useMemo(() => {
    if (!blogPosts.length) return [] as typeof blogPosts;

    return [...blogPosts].sort((a, b) => {
      const da = a.date ? new Date(a.date).getTime() : 0;
      const db = b.date ? new Date(b.date).getTime() : 0;
      if (isNaN(da) || isNaN(db)) return 0;
      return db - da;
    });
  }, [blogPosts]);

  // Show latest 6 in the homepage carousel
  const visiblePosts = useMemo(
    () => sortedBlogs.slice(0, 6),
    [sortedBlogs]
  );

  return (
    <section
      id="blogs-section"
      className="py-20 text-white overflow-hidden bg-[#050816] font-inter relative"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-10">
          <p className="text-xs uppercase tracking-[0.3em] text-cyan-400/80 mb-3">
            Insights & Strategies
          </p>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-extrabold mb-3">
            {blog?.title}
          </h2>
          <p className="text-sm md:text-base text-slate-400 max-w-2xl mx-auto">
            Stay ahead of the game with our latest casino guides, strategy
            breakdowns, and market insights.
          </p>
        </div>

        {/* Carousel */}
        <div className="relative" aria-label="Latest blog posts carousel">
          <div className="flex gap-4 overflow-x-auto pb-4 snap-x snap-mandatory scrollbar-thin scrollbar-thumb-slate-700/80 scrollbar-track-transparent">
            {visiblePosts.map((post, index) => {
              const globalIndex = sortedBlogs.findIndex((b) => b === post);

              return (
                <Link
                  key={index}
                  href={globalIndex >= 0 ? `/blog-details?id=${globalIndex}` : "#"}
                  className="snap-start min-w-[85%] sm:min-w-[55%] lg:min-w-[33%] group text-left bg-gradient-to-b from-[#151531] to-[#050816] border border-[#26264a] rounded-2xl p-5 md:p-6 shadow-xl shadow-black/40 transition-transform duration-300 hover:scale-[1.02] focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-500/60 flex flex-col justify-between"
                >
                  <div className="flex items-start justify-between gap-3 mb-3">
                    <span className="inline-block bg-[#17172f]/90 text-sky-300 text-[11px] font-semibold px-3 py-1 rounded-full shadow-md border border-sky-500/20">
                      {post.category}
                    </span>
                    {post.date && (
                      <span className="text-[11px] text-slate-300/90 whitespace-nowrap">
                        {post.date}
                      </span>
                    )}
                  </div>

                  <div className="space-y-2">
                    <h3 className="text-base md:text-lg font-semibold leading-snug group-hover:text-sky-300">
                      {post.title}
                    </h3>

                    <p className="text-xs md:text-sm text-slate-300/90 line-clamp-3">
                      {post.excerpt}
                    </p>
                  </div>

                  <div className="mt-4 flex items-center justify-between text-[11px] md:text-xs font-semibold text-sky-400">
                    <span className="inline-flex items-center gap-1">
                      Read full insight
                      <span className="transition-transform duration-200 group-hover:translate-x-0.5">
                        
                      </span>
                    </span>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>

        {/* Footer Text */}
        <div className="text-center pt-16 space-y-4">
          <p className="text-slate-400 text-sm">
            Explore our casino guides, betting strategies, and updates. These
            articles help players make informed decisions while enjoying
            responsible gaming.
          </p>
          {blogPosts.length > visiblePosts.length && (
            <Link
              href="/blogs"
              className="inline-flex items-center justify-center px-6 py-2 text-sm font-semibold rounded-full border border-cyan-500/60 text-cyan-300 hover:bg-cyan-500/10 transition-colors"
            >
              View more
            </Link>
          )}
        </div>
      </div>
    </section>
  );
};

export default BlogsSection;
